import classnames from 'classnames';
import {List} from 'immutable';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Anchor, Icon, Menu, Pagination, Text, WithTooltip} from '@albert-io/atomic';
import type {ClassroomModelV1, SubjectModelV2} from '@albert-io/models';
import {getActiveClassroomsWithSubjectsQuery} from 'client/generic/SubjectMenuRenderer/helpers/queries';
import {invalidatePartialInterest} from 'resources/mandark.resource';

import FreePracticeOptionToggle from './FreePracticeOptionToggle';

interface FreePracticeOptionsHeaderProps {
  [key: string]: unknown;
}

const FreePracticeOptionsHeader = (props: FreePracticeOptionsHeaderProps) => (
  <Menu.Item readonly disableHover className='flex justify-between' {...props}>
    <Text size='s' color='tertiary'>
      Allow independent practice
    </Text>
    <WithTooltip
      content="By allowing independent practice, your students can practice and view answers to any question found under this subject's Practice tab."
      wrapperProps={{className: 'pointer-events-auto'}}
    >
      <Icon icon={['far', 'question-circle']} />
    </WithTooltip>
  </Menu.Item>
);

interface FreePracticeOptionsProps {
  className?: string;
  classrooms?: List<ClassroomModelV1>;
  isAssessmentsOnly?: boolean;
  isUserLicensedTeacher?: boolean;
  subjectId: string;
  [key: string]: any;
}

const FreePracticeOptions = ({
  className,
  classrooms = List(),
  isAssessmentsOnly = false,
  subjectId,
  isUserLicensedTeacher = false,
  ...rest
}: FreePracticeOptionsProps) => {
  const queriesToInvalidateRef = useRef<Record<string, () => void>>({});
  const [page, setPage] = useState(1);

  useEffect(() => {
    const queries = queriesToInvalidateRef.current;

    return () => {
      if (Object.values(queries).length > 0) {
        invalidatePartialInterest({
          resourcePath: getActiveClassroomsWithSubjectsQuery().done().resourcePath
        });
        Object.values(queries).forEach((fn) => fn());
      }
    };
  }, []);

  const isClassroomLicensed = useCallback(
    (classroom: ClassroomModelV1) => {
      const licensedSubjects = classroom.getMeta().getSchoolLicensedSubjects();
      const groupLicensedSubjects = licensedSubjects.get('subject_group_subject_ids');
      const standaloneLicensedSubjects = licensedSubjects.get(
        'standalone_school_license_subject_ids'
      );
      return (
        groupLicensedSubjects.includes(subjectId) || standaloneLicensedSubjects.includes(subjectId)
      );
    },
    [subjectId]
  );

  const classroomsAndStatuses = useMemo(
    () =>
      classrooms
        .sort((a: ClassroomModelV1, b: ClassroomModelV1) => {
          if (isClassroomLicensed(a) && !isClassroomLicensed(b)) {
            return -1;
          }
          if (!isClassroomLicensed(a) && isClassroomLicensed(b)) {
            return 1;
          }
          return a.getName().localeCompare(b.getName());
        })
        .reduce(
          (prev: List<[ClassroomModelV1, boolean]> | undefined, curr: ClassroomModelV1) => {
            const accumulator = prev ?? List<[ClassroomModelV1, boolean]>();
            let checked: boolean;
            const currentSubject = curr
              .getSubjects()
              .find((s: SubjectModelV2) => s.getId() === subjectId);
            const classroomAndSubjectAreUnrelated = currentSubject === undefined;

            if (classroomAndSubjectAreUnrelated) {
              checked = false;
            } else {
              checked =
                currentSubject.getClassroomRelationships(curr.getId()).getRestrictFreePractice() !==
                true;
            }

            return accumulator.push([curr, checked]);
          },
          List() as List<[ClassroomModelV1, boolean]>
        ) as List<[ClassroomModelV1, boolean]>,
    [classrooms, subjectId, isClassroomLicensed]
  );

  const isSubjectLicensed = useMemo(
    () => classrooms.filter(isClassroomLicensed).size !== 0,
    [classrooms, isClassroomLicensed]
  );

  const handleChange = useCallback((classroomId: string) => {
    queriesToInvalidateRef.current[classroomId] = () =>
      invalidatePartialInterest({
        resourcePath: ['classrooms_v1', classroomId]
      });
  }, []);

  const pageSize = 10;

  const start = page * pageSize - pageSize;
  const end = start + pageSize;

  const totalPages = Math.ceil(classroomsAndStatuses.size / pageSize);
  const classroomsAndStatusesToShow = classroomsAndStatuses
    .map((entry?: [ClassroomModelV1, boolean]) => entry![0].getId())
    .slice(start, end);

  if (!isUserLicensedTeacher) {
    return (
      <div className={classnames(className)} {...rest}>
        <FreePracticeOptionsHeader />
        <Menu.Divider fullWidth />
        <Menu.Item readonly>
          <Text as='p' size='s' className='m-x2'>
            You can provide students with independent practice for any classroom that is associated
            with a school with an active school license.{' '}
            <Anchor to='/account/access'>View instructional license details.</Anchor>
            &nbsp;
          </Text>
        </Menu.Item>
      </div>
    );
  }

  if (classrooms.size === 0 || !isSubjectLicensed) {
    return (
      <div className={classnames(className)} {...rest}>
        <FreePracticeOptionsHeader />
        <Menu.Divider fullWidth />
        <Menu.Item readonly>
          <Text as='p' size='s' className='m-x2'>
            Start by making a class in your <Anchor to='/classes'>class dashboard.</Anchor>
          </Text>
        </Menu.Item>
      </div>
    );
  }

  return (
    <div className={classnames(className)} {...rest}>
      <FreePracticeOptionsHeader />
      <Menu.Divider fullWidth />
      {isAssessmentsOnly ? (
        <Menu.Item readonly>
          <Text
            as='div'
            className='cursor-auto p-x2'
            color='tertiary'
            italic
            onClick={(e: React.MouseEvent) => e.preventDefault()}
          >
            Not available for state assessments
          </Text>
        </Menu.Item>
      ) : (
        <>
          {classroomsAndStatuses.map((entry?: [ClassroomModelV1, boolean], idx?: number) => {
            const [classroom, checked] = entry!;
            const hidden = !classroomsAndStatusesToShow.includes(classroom.getId());
            const shouldDisable = !isClassroomLicensed(classroom);
            return (
              <React.Fragment key={classroom.getId()}>
                <Menu.Item
                  preventDefaultClickHandler={false}
                  disableHover={shouldDisable}
                  disableActive={shouldDisable}
                  aria-hidden={hidden}
                  className={classnames('block!', {hidden})}
                  onClick={shouldDisable ? undefined : () => handleChange(classroom.getId())}
                >
                  <FreePracticeOptionToggle
                    classroom={classroom}
                    defaultChecked={checked}
                    subjectId={subjectId}
                    disabled={shouldDisable}
                  />
                </Menu.Item>
                {idx !== classroomsAndStatuses.size - 1 && <Menu.Divider fullWidth />}
              </React.Fragment>
            );
          })}
          {classroomsAndStatuses.size > pageSize && (
            <Menu.Item preventDefaultClickHandler={false} disableHover disableActive>
              <Pagination
                variation='mini'
                align='center'
                defaultPage={page}
                key={`${page}+${totalPages}`}
                onChange={(val: number) => setPage(val)}
                totalPages={totalPages}
              />
            </Menu.Item>
          )}
        </>
      )}
    </div>
  );
};

export default FreePracticeOptions;
